<template>
  <div class="projects-table">
    <!-- Card unica: toolbar (ricerca, filtri attivi, conteggio, nuovo processo) + tabella.
         Il filtro status si sceglie nella pipeline della dashboard: qui compare solo come chip rimovibile. -->
    <v-card ref="projectsCard" class="projects-card" variant="flat" border :style="tableHeightStyle">
      <div class="projects-toolbar d-flex align-center flex-wrap ga-2 px-3 py-2">
        <v-text-field
          v-model="searchText"
          :placeholder="$t('projects.search')"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          class="projects-toolbar__search"
        ></v-text-field>
        <!-- Solo le fasi del mio profilo. Acceso di default per chi non opera tutto il flusso;
             con la policy "hidden" non serve, perché il Backend ha già tolto gli altri processi. -->
        <v-chip
          v-if="showScopeToggle"
          size="small"
          :color="projectStore.onlyMyStatuses ? 'primary' : undefined"
          :variant="projectStore.onlyMyStatuses ? 'flat' : 'outlined'"
          prepend-icon="mdi-account-check-outline"
          @click="projectStore.setScopeFilter({ onlyMine: !projectStore.onlyMyStatuses })"
        >
          {{ $t('dashboard.onlyMine') }}
        </v-chip>

        <!-- Filtro per utente (admin): la lista vista con gli occhi del suo profilo -->
        <v-select
          v-if="showUserFilter"
          v-model="scopeUser"
          :items="userScopeItems"
          item-title="title"
          item-value="value"
          :label="$t('dashboard.filterByUser')"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          class="projects-toolbar__user"
        ></v-select>

        <v-chip
          v-if="activeGroupChip"
          size="small"
          color="primary"
          variant="tonal"
          closable
          :close-label="$t('projects.clearFilter')"
          @click:close="clearGroupFilter"
        >
          {{ activeGroupChip }}
        </v-chip>
        <v-chip
          v-if="activeStatusChip"
          size="small"
          color="primary"
          variant="tonal"
          closable
          :close-label="$t('projects.clearFilter')"
          @click:close="clearStatusFilter"
        >
          <span v-if="activeStatusChip.icon" class="mr-1">{{ activeStatusChip.icon }}</span>{{ activeStatusChip.descr }}
        </v-chip>
        <v-spacer></v-spacer>
        <span class="text-caption text-medium-emphasis">
          {{ $t('projects.countLabel', { n: filteredProjects.length }) }}<template v-if="lastRefreshLabel"> · {{ $t('projects.updatedAt', { time: lastRefreshLabel }) }}</template>
        </span>
        <v-btn
          v-if="canCreateProject"
          color="primary"
          size="small"
          variant="flat"
          prepend-icon="mdi-plus"
          class="d-none d-md-inline-flex"
          @click="openCreateDialog"
        >
          {{ $t('projects.createProject') }}
        </v-btn>
      </div>
      <!-- Altezza limitata allo spazio che resta nella finestra (vedi fitTableToWindow): la tabella
           scorre al suo interno, così la barra orizzontale e la paginazione restano a vista anche in
           una finestra stretta, invece di comparire solo dopo l'ultima riga. -->
      <v-data-table
        v-model:expanded="expanded"
        show-expand
        fixed-header
        density="compact"
        :headers="headers"
        :items="filteredProjects"
        :loading="projectStore.loading"
        :search="searchText"
        :items-per-page="projectStore.pageSize"
        :server-items-length="projectStore.totalProjects"
        item-value="idProject"
        :sort-by="defaultSortBy"
        @update:options="onTableOptionsUpdate"
        @click:row="viewProject"
        hover
      >
        <!-- Loading state -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
        </template>

        <!-- No data state -->
        <template v-slot:no-data>
          <div class="text-center pa-4">
            <v-icon size="48" color="grey">mdi-folder-open-outline</v-icon>
            <p class="mt-2 text-grey">{{ $t('projects.noProjects') }}</p>
          </div>
        </template>

        <!-- Row styling for overdue items.
             Le celle sono generate dalle colonne configurate (columns.json), ma il <tr> resta scritto
             qui: se lo rendesse Vuetify non porterebbe l'attributo data-v-* e le regole scoped di
             colorazione riga smetterebbero di applicarsi senza alcun errore visibile. -->
        <template v-slot:item="{ item, internalItem, isExpanded, toggleExpand, columns }">
          <tr
            :class="{
              'warning-row': isWarning(item),
              'overdue-row': !isWarning(item) && isOverdue(item),
              'deadline-warn-row': projectDeadline(item).level === 'warn',
              'deadline-critical-row': projectDeadline(item).level === 'critical' || projectDeadline(item).level === 'overdue'
            }"
            @click="viewProject(null, { item })"
            style="cursor: pointer"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="cellClass(column, item)"
            >
              <!-- Pseudo-colonna: espansione, solo se c'è qualcosa da mostrare -->
              <template v-if="column.key === 'data-table-expand'">
                <v-btn
                  v-if="hasExpandableContent(item)"
                  :icon="isExpanded(internalItem) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  size="small"
                  variant="text"
                  @click.stop="toggleExpand(internalItem)"
                ></v-btn>
              </template>

              <!-- Pseudo-colonna: azioni -->
              <template v-else-if="column.key === 'actions'">
                <v-btn
                  icon
                  size="small"
                  @click.stop="openProjectFolder(item)"
                  :title="$t('projects.openFolder')"
                  :disabled="!localBackendStore.isConnected"
                >
                  <v-icon size="small">mdi-folder-open</v-icon>
                </v-btn>
              </template>

              <!-- Colonna "da compilare": testo troncato + tooltip con l'elenco completo.
                   Il ramo si attiva su qualunque colonna il cui header dichiari cellTooltip. -->
              <template v-else-if="column.cellTooltip === 'todo'">
                <span v-if="todoInfo(item).terminal" class="text-disabled">—</span>

                <v-tooltip v-else-if="todoInfo(item).missing.length === 0" location="top" open-delay="300">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="small" color="success">mdi-check-circle-outline</v-icon>
                  </template>
                  <span>{{ $t('projects.todo.allDone') }}</span>
                </v-tooltip>

                <v-tooltip v-else location="top" max-width="420" open-delay="300">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="todo-cell" :style="{ maxWidth: todoCellWidth(column) }">
                      {{ internalItem.columns[column.key] }}
                    </span>
                  </template>
                  <div class="text-caption font-weight-bold mb-1">
                    {{ $t('projects.todo.tooltipTitle', { status: todoInfo(item).nextStates.map(s => s.descr).join(' / ') }) }}
                  </div>
                  <ul class="pl-4 mb-0">
                    <li v-for="f in todoInfo(item).missing" :key="f.name">
                      {{ f.label }}
                      <v-icon v-if="f.requiresVerification" size="x-small" class="ml-1">mdi-robot-outline</v-icon>
                    </li>
                  </ul>
                  <div
                    v-if="todoInfo(item).missing.some(f => f.requiresVerification)"
                    class="text-caption mt-1 text-medium-emphasis"
                  >
                    <v-icon size="x-small" class="mr-1">mdi-robot-outline</v-icon>{{ $t('projects.todo.verificationHint') }}
                  </div>
                </v-tooltip>
              </template>

              <!-- Colonna status: pallino nel colore dello status + descrizione, come nella pipeline -->
              <template v-else-if="column.cellRender === 'status'">
                <span class="status-cell">
                  <span class="status-cell__dot" :style="{ backgroundColor: getStatusColor(item.activeStatus, item.projectType) }"></span>
                  {{ internalItem.columns[column.key] }}
                </span>
              </template>

              <!-- Follow-up: data + "hoje" / "há N dias". Il colore resta su questa cella sola (cellHighlight),
                   la riga porta soltanto la striscia laterale. -->
              <template v-else-if="column.cellHighlight === 'reminderAlert'">
                {{ internalItem.columns[column.key] }}
                <span v-if="reminderSuffix(item)" class="text-caption ml-1">· {{ reminderSuffix(item) }}</span>
              </template>

              <!-- Colonna dati: valore già calcolato dal formatter dell'header e messo in cache da
                   Vuetify, così testo mostrato, ricerca e ordinamento usano la stessa stringa -->
              <template v-else>{{ internalItem.columns[column.key] }}</template>
            </td>
          </tr>
        </template>

        <!-- Expanded Row Content -->
        <template v-slot:expanded-row="{ columns, item }">
          <tr>
            <td :colspan="columns.length" class="pa-4 bg-grey-lighten-5">
              <!-- Numerary Information -->
              <div v-if="item.numeraryValue" class="mb-4">
                <v-chip color="teal" variant="flat" size="small" class="mr-2">
                  <v-icon start size="x-small">mdi-cash-multiple</v-icon>
                  {{ $t('projects.fields.numeraryValue') || 'Valor do Numerário' }}: 
                  <span class="ml-1 font-weight-bold">R$ {{ item.numeraryValue }}</span>
                </v-chip>
                <v-chip v-if="item.numeraryValueDate" color="teal" variant="outlined" size="small">
                  <v-icon start size="x-small">mdi-calendar-check</v-icon>
                  {{ $t('projects.fields.numeraryValueDate') || 'Data Deposito' }}: 
                  <span class="ml-1">{{ formatDate(item.numeraryValueDate) }}</span>
                </v-chip>
              </div>

              <div v-if="item.annotations && item.annotations.length > 0">
                <div class="text-subtitle-2 font-weight-bold mb-3">
                  <v-icon size="small" class="mr-1">mdi-note-text-outline</v-icon>
                  Notas do Processo:
                </div>
                <div class="d-flex flex-column ga-2">
                  <v-card 
                    v-for="(note, index) in [...item.annotations].reverse()" 
                    :key="index"
                    variant="flat" 
                    border 
                    class="pa-3 bg-white mb-2"
                  >
                    <div class="d-flex justify-space-between align-center mb-2">
                      <v-chip 
                        size="x-small" 
                        :color="getStatusColor(note.statusId, item.projectType)"
                        class="font-weight-bold"
                      >
                        {{ note.statusDescription }}
                      </v-chip>
                      <span class="text-caption text-grey">
                        <v-icon size="x-small" class="mr-1">mdi-account</v-icon>
                        {{ note.user }} 
                        <span class="mx-1">•</span>
                        <v-icon size="x-small" class="mr-1">mdi-clock-outline</v-icon>
                        {{ formatDateTime(note.timestamp) }}
                      </span>
                    </div>
                    <div class="text-body-2" style="white-space: pre-wrap;">{{ note.text }}</div>
                  </v-card>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Actions Menu -->
    <v-menu v-model="showActionsMenu" :activator="actionsMenuActivator">
      <v-list>
        <v-list-item @click="viewProject(selectedProjectForActions)">
          <template v-slot:prepend>
            <v-icon>mdi-eye</v-icon>
          </template>
          <v-list-item-title>Visualizar</v-list-item-title>
        </v-list-item>
        
        <v-list-item @click="editProject(selectedProjectForActions)">
          <template v-slot:prepend>
            <v-icon>mdi-pencil</v-icon>
          </template>
          <v-list-item-title>Editar</v-list-item-title>
        </v-list-item>
        
        <v-divider></v-divider>
        
        <v-list-item @click="deleteProject(selectedProjectForActions)" class="text-red">
          <template v-slot:prepend>
            <v-icon color="red">mdi-delete</v-icon>
          </template>
          <v-list-item-title>Deletar</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Project Form Dialog -->
    <ProjectForm
      v-model="showProjectForm"
      :project="selectedProject"
      :is-editing="isEditing"
      @project-saved="onProjectSaved"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('projects.confirmDelete') }}</span>
        </v-card-title>
        <v-card-text>
          {{ $t('projects.deleteMessage', { reference: projectToDelete?.reference }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showDeleteDialog = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="red" variant="text" @click="confirmDelete" :loading="deleting">
            {{ $t('common.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from '../stores/projects.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import ProjectForm from './ProjectForm.vue'
import { useI18n } from 'vue-i18n'
import { calculateBusinessDays, now } from '../utils/dateUtils.js'
import { followUpState, overdueDays } from '../utils/followUp.js'
import { useDeadlines } from '../composables/useDeadlines.js'
import { useFieldRequirements } from '../composables/useFieldRequirements.js'
import { useWorkflowAccess } from '../composables/useWorkflowAccess.js'
import { apiCall } from '../utils/api.js'
import { POLICY_HIDDEN } from '../utils/workflowAccess.js'
import { useAiCostStore } from '../stores/aiCost.js'
import { formatUsdShort } from '../utils/money.js'

const route = useRoute()
const router = useRouter()

const projectStore = useProjectStore()
const aiCostStore = useAiCostStore()
const localBackendStore = useLocalBackendStore()
const { t, te } = useI18n()
const { projectDeadline } = useDeadlines()
const { missingFieldsForNextStatus } = useFieldRequirements()
const {
  isAdmin,
  hasRoleScopes,
  policy: rolePolicy,
  roleLabel,
  statusRoleLabels,
  canCreateProject
} = useWorkflowAccess()

// Define emits
const emit = defineEmits(['project-saved'])

// Estado local
const searchText = ref('')
const showProjectForm = ref(false)
const showDeleteDialog = ref(false)
const selectedProject = ref(null)
const projectToDelete = ref(null)
const isEditing = ref(false)
const deleting = ref(false)
const showActionsMenu = ref(false)
const actionsMenuActivator = ref(null)
const selectedProjectForActions = ref(null)
const expanded = ref([])

// ---- ambito per profilo ----

// Il toggle serve solo a chi opera una parte del flusso e vede comunque tutto il resto
const showScopeToggle = computed(() =>
  hasRoleScopes.value && !isAdmin.value && rolePolicy.value !== POLICY_HIDDEN)

const showUserFilter = computed(() => isAdmin.value && hasRoleScopes.value)

// Utenti per il filtro admin. Si sceglie una persona, si filtra per il suo profilo: due persone
// con lo stesso profilo lavorano le stesse fasi, ed è quello che la lista deve mostrare.
const directoryUsers = ref([])

const userScopeItems = computed(() =>
  directoryUsers.value
    .filter(u => u.role && u.role !== 'superadmin' && u.role !== 'admin')
    .map(u => ({ title: `${u.username} · ${roleLabel(u.role)}`, value: u.username })))

// Persona scelta nel filtro. Si filtra per il suo profilo: due persone con lo stesso profilo
// lavorano le stesse fasi, quindi la lista che interessa e' la stessa.
const scopeUsername = ref(null)

const scopeUser = computed({
  get: () => scopeUsername.value,
  set: (username) => {
    scopeUsername.value = username || null
    const user = directoryUsers.value.find(u => u.username === username)
    projectStore.setScopeFilter({ role: user?.role || null })
  }
})

const loadDirectory = async () => {
  if (!isAdmin.value) return
  try {
    const response = await apiCall('/api/users/directory')
    directoryUsers.value = response?.users || []
  } catch (e) {
    // Senza directory si perde solo il filtro per utente: la tabella resta quella di sempre
    console.warn('Lista utenti non disponibile per il filtro:', e?.message)
  }
}


// Ora dell'ultimo caricamento riuscito, per la toolbar ("atualizado às hh:mm")
const lastRefreshAt = ref(null)
watch(() => projectStore.projects, () => { lastRefreshAt.value = new Date() })
const lastRefreshLabel = computed(() =>
  lastRefreshAt.value ? lastRefreshAt.value.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''
)

// Filtri attivi mostrati come chip rimovibili: status e gruppo arrivano dalla dashboard via store
const activeStatusChip = computed(() => {
  const status = projectStore.statusFilter
  if (status === null || status === undefined) return null
  const info = projectStore.getStatusInfo(String(status), projectStore.projectTypeFilter)
  return info ? { icon: info.icon, descr: info.descr } : { icon: null, descr: String(status) }
})

const activeGroupChip = computed(() => {
  const group = projectStore.groupFilter
  if (group === null || group === undefined) return null
  const key = 'dashboard.groups.' + group
  return te(key) ? t(key) : group
})

const clearStatusFilter = () => projectStore.setStatusFilter(null)
const clearGroupFilter = () => {
  projectStore.setGroupFilter(null)
  projectStore.setStatusFilter(null)
}

// Colonne usate se columns.json manca, è malformato o non ha un set utilizzabile: identiche a quelle
// storiche, così cancellare il file degrada alla tabella di sempre invece che a una griglia vuota.
const DEFAULT_COLUMN_SET = {
  defaultSort: { key: 'reminderDate', order: 'asc' },
  columns: [
    { key: 'data-table-expand', width: 48, sortable: false, formatter: 'none' },
    { key: 'idProject', i18nKey: 'projects.table.id', width: 150, formatter: 'raw' },
    { key: 'reference', i18nKey: 'projects.table.reference', width: 120, formatter: 'raw' },
    { key: 'transportType', i18nKey: 'projects.table.transportType', width: 120, formatter: 'transportType' },
    { key: 'servicesProvided', i18nKey: 'projects.table.services', width: 120, formatter: 'services' },
    { key: 'activeStatus', i18nKey: 'projects.table.status', width: 140, formatter: 'status' },
    { key: 'daysDelayed', i18nKey: 'projects.table.daysDelayed', width: 110, formatter: 'daysDelayed' },
    { key: 'reminderDate', i18nKey: 'projects.table.dueDate', width: 110, formatter: 'date', cellHighlight: 'reminderAlert' },
    { key: 'estimatedDepartureDate', i18nKey: 'projects.table.estimatedDeparture', width: 110, formatter: 'date' },
    { key: 'estimatedArriveDate', i18nKey: 'projects.table.estimatedArrival', width: 110, formatter: 'date' },
    { key: 'numeraryValueDate', i18nKey: 'projects.table.numeraryDate', width: 110, formatter: 'date' },
    { key: 'actions', i18nKey: 'projects.table.actions', width: 80, sortable: false, formatter: 'none' }
  ]
}

// Colonne senza un dato corrispondente nel processo: gestite da rami dedicati nel template
const PSEUDO_COLUMN_KEYS = new Set(['data-table-expand', 'actions'])

// Computed
const filteredProjects = computed(() => {
  return projectStore.filteredProjects
})

// Métodos
const formatDate = (dateString) => {
  if (!dateString) return '-'
  
  // Extract just the date part (YYYY-MM-DD) to avoid timezone conversion issues
  const datePart = dateString.split('T')[0]
  const [year, month, day] = datePart.split('-')
  
  // Create date using local timezone (no conversion)
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  return date.toLocaleDateString('pt-BR')
}

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '-'
  const date = new Date(dateTimeString)
  return date.toLocaleString('pt-BR')
}

const calculateDaysDelayed = (statusChangeDate) => {
  if (!statusChangeDate) return '-'
  
  // Parse the date string correctly
  const datePart = statusChangeDate.split('T')[0]
  const [year, month, day] = datePart.split('-')
  const changeDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  
  // Get today's date
  const today = now()
  today.setHours(0, 0, 0, 0)
  
  // Calculate business days between changeDate and today
  const businessDays = calculateBusinessDays(changeDate, today)
  
  return businessDays
}

// Le regole vivono in utils/followUp.js: le usa anche la pagina del carico di lavoro
const statusOf = (project) =>
  projectStore.getStatusInfo(String(project.activeStatus), project.projectType)

const isWarning = (project) => followUpState(project, statusOf(project)) === 'today'

const isOverdue = (project) => followUpState(project, statusOf(project)) === 'overdue'

const getStatusInfo = (statusId, projectTypeId = null) => {
  return projectStore.getStatusInfo(statusId, projectTypeId)
}

const getStatusDescription = (statusId, projectTypeId = null) => {
  const status = getStatusInfo(statusId, projectTypeId)
  return status?.descr || 'Desconhecido'
}

const getStatusIcon = (statusId, projectTypeId = null) => {
  const status = getStatusInfo(statusId, projectTypeId)
  return status?.icon || '❓'
}

const getStatusColor = (statusId, projectTypeId = null) => {
  const status = getStatusInfo(statusId, projectTypeId)
  return status?.color || '#9e9e9e'
}

// Suffisso relativo del follow-up: "hoje" se scade oggi, "há N dias" se scaduto, niente se futuro
const reminderSuffix = (project) => {
  if (isWarning(project)) return t('projects.table.dueToday')
  if (!isOverdue(project)) return ''
  return t('projects.table.overdueBy', { n: Math.max(1, overdueDays(project)) })
}

const getProjectTypeDescription = (typeId) => {
  if (typeId === null || typeId === undefined) return '-'
  const type = projectStore.getProjectTypeInfo(typeId)
  return type?.descr || 'Desconhecido'
}

const formatServices = (services) => {
  if (!services || !Array.isArray(services) || services.length === 0) return '-'
  // Convert array to uppercase string, separated by commas
  return services.map(s => s.toUpperCase()).join(', ')
}

const formatTransportType = (type) => {
  if (!type) return '-'
  return type.toUpperCase()
}

// ---------- Colonne configurabili (columns.json) ----------

// Le funzioni non sono serializzabili: columns.json porta solo il nome del formatter e qui si risolve.
// Le chiavi devono restare allineate a AllowedColumnFormatters in ProjectsController.cs.
const CELL_FORMATTERS = {
  raw: (item, key) => item[key],
  date: (item, key) => formatDate(item[key]),
  dateTime: (item, key) => formatDateTime(item[key]),
  services: (item, key) => formatServices(item[key]),
  transportType: (item, key) => formatTransportType(item[key]),
  status: (item) => getStatusDescription(item.activeStatus, item.projectType),
  statusIcon: (item) => getStatusIcon(item.activeStatus, item.projectType),
  daysDelayed: (item) => calculateDaysDelayed(item.statusChangeDate),
  projectType: (item) => getProjectTypeDescription(item.projectType),
  currency: (item, key) => (item[key] === null || item[key] === undefined || item[key] === '' ? '-' : `R$ ${item[key]}`),
  // Campi ancora da compilare per avanzare di stato. Il troncamento è CSS, non qui: il valore
  // intero resta nella cella così anche la ricerca lo trova. Il tooltip con l'elenco completo
  // è nel ramo dedicato dello slot item.
  todoList: (item) => {
    const info = missingFieldsForNextStatus(item)
    if (info.terminal || info.missing.length === 0) return '-'
    return info.missing.map(f => f.label).join(', ')
  },
  // Costo IA a vita del processo. Il valore non sta nel processo ma nella mappa dello store
  // dedicato: qui si legge soltanto.
  aiCost: (item) => {
    const usd = aiCostStore.costOf(item.idProject)
    return usd == null ? '-' : formatUsdShort(usd)
  },
  // Profili che operano la fase corrente. Vuoto significa "di tutti", e si mostra come tale.
  statusRoles: (item) => {
    const labels = statusRoleLabels(item.projectType, item.activeStatus)
    return labels.length ? labels.join(', ') : t('projects.table.statusRolesAll')
  },
  none: () => ''
}

// Formatter il cui output non è ordinabile come testo. Ogni voce costruisce il comparatore sui dati
// grezzi della riga: le date sull'ISO (dd/mm/yyyy si ordinerebbe per giorno del mese), "todo" sul
// NUMERO di campi mancanti (crescente = più vicini a poter avanzare).
const SORT_RAW_BUILDERS = {
  date: (col) => (a, b) => String(a?.[col.key] ?? '').localeCompare(String(b?.[col.key] ?? '')),
  dateTime: (col) => (a, b) => String(a?.[col.key] ?? '').localeCompare(String(b?.[col.key] ?? '')),
  todoList: () => (a, b) =>
    missingFieldsForNextStatus(a).missing.length - missingFieldsForNextStatus(b).missing.length,
  // Senza questo la colonna ordinerebbe la stringa formattata: "US$ 9,00" verrebbe dopo "US$ 10,00".
  aiCost: () => (a, b) =>
    (aiCostStore.costOf(a?.idProject) || 0) - (aiCostStore.costOf(b?.idProject) || 0)
}

// Classi condizionali di cella: non esprimibili in JSON, il file porta solo il nome.
// Allineate a AllowedCellHighlights in ProjectsController.cs.
const CELL_HIGHLIGHTS = {
  reminderAlert: (item) => ({
    'text-orange font-weight-bold': isWarning(item),
    'text-red font-weight-bold': !isWarning(item) && isOverdue(item)
  })
}

// Set di colonne del tipo di processo attivo, con fallback al set 'default' e poi a quello interno
const activeColumnSet = computed(() => {
  const cfg = projectStore.tableColumns
  const byType = cfg?.byProjectType || {}
  const filter = projectStore.projectTypeFilter
  const candidate = (filter !== null && filter !== undefined ? byType[String(filter)] : null) || cfg?.default
  return Array.isArray(candidate?.columns) && candidate.columns.length ? candidate : DEFAULT_COLUMN_SET
})

const visibleColumns = computed(() =>
  (activeColumnSet.value.columns || []).filter(c => c.visible !== false)
)

// Etichetta di un campo da fields.json, come ultima risorsa prima di mostrare la chiave grezza
const fieldLabelFor = (key) => {
  const fields = projectStore.fields
  if (!Array.isArray(fields)) return null
  const typeId = projectStore.projectTypeFilter
  const match = fields.find(f => f.name === key &&
    (typeId === null || typeId === undefined || (f.visibleInProjectTypes || []).includes(typeId)))
  return match?.label || null
}

// Precedenza: etichetta personalizzata → i18nKey del file → convenzione projects.table.<key> →
// etichetta del campo in fields.json → chiave. Serve un i18nKey esplicito perché per 7 colonne su 12
// la chiave i18n non coincide con la chiave colonna (reminderDate → dueDate, idProject → id, ...).
const columnTitle = (col) => {
  if (col.label) return col.label
  if (col.i18nKey && te(col.i18nKey)) return t(col.i18nKey)
  const conventional = `projects.table.${col.key}`
  if (te(conventional)) return t(conventional)
  return fieldLabelFor(col.key) || col.key
}

// Chiavi già segnalate, per non intasare la console con un warning per riga
const warnedUnknownFormatters = new Set()

const resolveFormatter = (col) => {
  const name = col.formatter || 'raw'
  const fn = CELL_FORMATTERS[name]
  if (fn) return fn
  if (!warnedUnknownFormatters.has(name)) {
    warnedUnknownFormatters.add(name)
    console.warn(`[ProjectsTable] formatter sconosciuto "${name}" (colonna "${col.key}"): uso "raw"`)
  }
  return CELL_FORMATTERS.raw
}

const headers = computed(() =>
  visibleColumns.value.map(col => {
    const isPseudo = PSEUDO_COLUMN_KEYS.has(col.key)
    const header = {
      key: col.key,
      title: col.key === 'data-table-expand' ? '' : columnTitle(col),
      width: col.width ? `${col.width}px` : undefined,
      sortable: !isPseudo && col.sortable !== false,
      align: col.align || undefined,
      // prop custom: Vuetify fa spread dell'header originale, quindi sopravvive fino allo slot item
      cellHighlight: col.cellHighlight || null
    }

    if (!isPseudo) {
      const format = resolveFormatter(col)
      header.value = (item) => format(item, col.key)

      const buildSortRaw = SORT_RAW_BUILDERS[col.formatter]
      if (buildSortRaw) header.sortRaw = buildSortRaw(col)

      // prop custom: attiva il ramo di render con tooltip nello slot item, stesso meccanismo di
      // cellHighlight. Così il ciclo sulle colonne resta uno solo e il <tr> non si sposta.
      if (col.formatter === 'todoList') header.cellTooltip = 'todo'
      // Stesso meccanismo: la colonna status disegna il pallino colorato accanto al testo
      if (col.formatter === 'status') header.cellRender = 'status'
    }

    return header
  })
)

// Ordinamento iniziale dal set attivo. Filtrato contro le colonne realmente visibili: un file
// modificato a mano potrebbe puntare a una colonna nascosta.
const defaultSortBy = computed(() => {
  const sort = activeColumnSet.value.defaultSort
  if (!sort?.key) return []
  const usable = headers.value.some(h => h.key === sort.key && h.sortable)
  if (!usable) return []
  return [{ key: sort.key, order: sort.order === 'desc' ? 'desc' : 'asc' }]
})

const hasExpandableContent = (item) =>
  (item.annotations && item.annotations.length > 0) || !!item.numeraryValue

const cellClass = (column, item) => {
  const classes = []
  const highlight = column.cellHighlight && CELL_HIGHLIGHTS[column.cellHighlight]
  if (highlight) classes.push(highlight(item))
  if (column.align) classes.push(`text-${column.align}`)
  return classes
}

// Alias per il template. Il risultato è memoizzato nel composable, quindi chiamarlo più volte
// per riga (e da sortRaw) non costa nulla.
const todoInfo = (item) => missingFieldsForNextStatus(item)

// La larghezza dell'header si applica solo al <th>: per l'ellissi serve un max-width sullo <span>
const todoCellWidth = (column) => {
  const width = column?.width
  if (typeof width === 'number') return `${width}px`
  if (typeof width === 'string' && width) return width
  return '220px'
}

const onTableOptionsUpdate = (options) => {
  // Implementar paginação se necessário
  if (options.page !== projectStore.currentPage) {
    projectStore.loadProjects(options.page, projectStore.statusFilter)
  }
}

// L'URL rispecchia il processo aperto (?open=<idProject>): un refresh o un link condiviso
// rientrano sullo stesso processo invece di tornare alla lista.
const syncOpenQuery = (id) => {
  const current = route.query.open ? String(route.query.open) : null
  const wanted = id ? String(id) : null
  if (current === wanted) return
  const query = { ...route.query }
  if (wanted) query.open = wanted
  else delete query.open
  router.replace({ query })
}

const openCreateDialog = () => {
  selectedProject.value = null
  isEditing.value = false
  showProjectForm.value = true
  // La creazione non ha ancora un id: la query resta pulita
  syncOpenQuery(null)
}

const viewProject = (event, row) => {
  const project = row?.item || event
  selectedProject.value = project
  isEditing.value = true
  showProjectForm.value = true
  syncOpenQuery(project?.idProject)
}

const editProject = (project) => {
  selectedProject.value = project
  isEditing.value = true
  showProjectForm.value = true
  syncOpenQuery(project?.idProject)
}

const deleteProject = (project) => {
  projectToDelete.value = project
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!projectToDelete.value) return
  
  try {
    deleting.value = true
    await projectStore.deleteProject(projectToDelete.value.idProject)
    showDeleteDialog.value = false
    projectToDelete.value = null
  } catch (error) {
    console.error('Erro ao deletar projeto:', error)
  } finally {
    deleting.value = false
  }
}

const onProjectSaved = () => {
  // Recarregar lista de projetos
  projectStore.loadProjects(projectStore.currentPage, projectStore.statusFilter)
  // Emitir evento para o componente pai
  emit('project-saved')
}

const openProjectFolder = async (project) => {
  if (!project?.idProject) return
  
  try {
    await projectStore.openProjectFolder(project.idProject)
    console.log('Folder opened for project:', project.idProject)
  } catch (error) {
    console.error('Error opening project folder:', error)
    // Opcionalmente potresti mostrare un messaggio di errore all'utente
  }
}

// Lifecycle
// Apre la vista di un progetto a partire dal suo id (es. dal segnale deadline in app bar)
const openProjectById = async (id) => {
  if (!id) return
  // Gia' aperto su questo processo: non ricaricare nulla (la query la scriviamo noi stessi)
  if (showProjectForm.value && isEditing.value && selectedProject.value?.idProject === id) return
  let project = (projectStore.allProjects || []).find(p => p.idProject === id)
    || (projectStore.projects || []).find(p => p.idProject === id)
  if (!project) {
    project = await projectStore.getProject(id)
  }
  if (project) {
    viewProject(project)
  } else {
    // Id inesistente o non accessibile: la query non deve restare appesa
    syncOpenQuery(null)
  }
}

// ---- altezza della tabella ----
// La tabella deve finire dentro la finestra: righe, barra di scorrimento orizzontale e paginazione
// visibili senza scorrere la pagina. Lo spazio dipende da quanto c'è sopra (pulsanti dei tipi,
// pipeline, avvisi di Home, toolbar che va a capo nelle finestre strette), quindi si misura.
const projectsCard = ref(null)
const tableMaxHeight = ref(null)
// Sotto questa altezza la tabella sarebbe inservibile: meglio far scorrere la pagina
const MIN_TABLE_HEIGHT = 240
// Spazio sotto la card: padding inferiore della dashboard + bordo della card + margine di sicurezza
const BOTTOM_GAP = 16 + 1 + 3

const tableHeightStyle = computed(() =>
  tableMaxHeight.value ? { '--projects-table-max-h': `${tableMaxHeight.value}px` } : {}
)

function fitTableToWindow() {
  const card = projectsCard.value?.$el
  const wrapper = card?.querySelector('.v-table__wrapper')
  if (!wrapper) return
  const footer = card.querySelector('.v-data-table-footer')
  // Posizione nel documento, non nella finestra: il calcolo non dipende da quanto si è già scorso
  const top = wrapper.getBoundingClientRect().top + window.scrollY
  const available = window.innerHeight - top - (footer?.offsetHeight || 0) - BOTTOM_GAP
  const next = Math.max(MIN_TABLE_HEIGHT, Math.floor(available))
  // Stesso valore: niente da toccare (evita di rimettere in moto l'osservatore)
  if (next !== tableMaxHeight.value) tableMaxHeight.value = next
}

let fitFrame = 0
function scheduleFit() {
  if (fitFrame) return
  fitFrame = requestAnimationFrame(() => {
    fitFrame = 0
    fitTableToWindow()
  })
}

// Si rimisura quando cambia l'altezza di ciò che sta sopra la tabella: gli avvisi di Home e la
// dashboard (figli di v-main), la toolbar della card. La pagina intera non basta: v-app è alta almeno
// quanto la finestra, quindi un avviso chiuso non la accorcia e la tabella resterebbe bassa.
let layoutObserver = null
let mainObserver = null

function observeLayout() {
  if (!layoutObserver) return
  layoutObserver.disconnect()
  const card = projectsCard.value?.$el
  if (!card) return
  layoutObserver.observe(document.body)
  const toolbar = card.querySelector('.projects-toolbar')
  if (toolbar) layoutObserver.observe(toolbar)
  const main = card.closest('.v-main')
  const container = main?.querySelector(':scope > .v-main__scroller') || main
  if (container) {
    for (const child of container.children) layoutObserver.observe(child)
  }
}

onMounted(() => {
  window.addEventListener('resize', scheduleFit)
  if (typeof ResizeObserver !== 'undefined') {
    layoutObserver = new ResizeObserver(scheduleFit)
    observeLayout()
    // Avvisi che compaiono o spariscono in v-main: vanno osservati anche loro
    const main = projectsCard.value?.$el?.closest('.v-main')
    if (main && typeof MutationObserver !== 'undefined') {
      mainObserver = new MutationObserver(() => {
        observeLayout()
        scheduleFit()
      })
      mainObserver.observe(main, { childList: true, subtree: false })
      const scroller = main.querySelector(':scope > .v-main__scroller')
      if (scroller) mainObserver.observe(scroller, { childList: true, subtree: false })
    }
  }
  nextTick(scheduleFit)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', scheduleFit)
  layoutObserver?.disconnect()
  mainObserver?.disconnect()
  if (fitFrame) cancelAnimationFrame(fitFrame)
})

onMounted(async () => {
  // Carregar configurações e projetos
  await projectStore.loadConfigurations()
  await projectStore.loadProjects()
  // Costo IA: non si aspetta: se tarda o fallisce, la colonna mostra un trattino.
  aiCostStore.loadCosts()
  // Lista utenti per il filtro admin: stessa logica, non si aspetta
  loadDirectory()
  // Apertura diretta richiesta via query (?open=<idProject>)
  if (route.query.open) {
    openProjectById(String(route.query.open))
  }
})

// Se siamo già sulla dashboard e cambia solo la query (click ripetuto dalla campana,
// oppure il tasto indietro del browser che toglie ?open=)
watch(() => route.query.open, (id) => {
  if (id) openProjectById(String(id))
  else if (showProjectForm.value && isEditing.value) showProjectForm.value = false
})

// Chiusura della scheda: l'URL torna alla lista
watch(showProjectForm, (val) => {
  if (!val) syncOpenQuery(null)
})

// Expose methods to parent components
defineExpose({
  openCreateDialog
})
</script>

<style scoped>
.projects-table {
  width: 100%;
}

/* Colonna "da compilare": una riga sola, troncata con ellissi alla larghezza configurata */
.todo-cell {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* ---- card e toolbar ---- */
.projects-card {
  border-radius: 8px;
  overflow: hidden;
}

.projects-toolbar {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.projects-toolbar__search {
  flex: 0 1 320px;
  min-width: 200px;
}

.projects-toolbar__user {
  flex: 0 1 240px;
  min-width: 180px;
}

/* La tabella scorre al suo interno, alta al massimo lo spazio che resta nella finestra
   (misurato in fitTableToWindow). Il valore di riserva vale finché la misura non è pronta. */
.projects-card :deep(.v-table__wrapper) {
  max-height: var(--projects-table-max-h, max(240px, calc(100vh - 150px)));
}

.projects-card :deep(.v-data-table__th) {
  font-size: 11.5px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  /* Opaco: con l'intestazione fissa le righe ci scorrono sotto. Velatura grigia sopra il colore pieno. */
  background:
    linear-gradient(rgba(var(--v-theme-on-surface), 0.02), rgba(var(--v-theme-on-surface), 0.02)),
    rgb(var(--v-theme-surface));
  white-space: nowrap;
}

/* Status: pallino nel colore di cfg.json, come i chip della pipeline */
.status-cell {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}

.status-cell__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

/* ---- severità di riga: tinta leggera + striscia sulla prima cella, non più il riempimento pieno ---- */
.warning-row td {
  background-color: rgba(255, 179, 0, 0.07) !important;
}
.warning-row td:first-child {
  box-shadow: inset 3px 0 0 0 #ffb300;
}
.warning-row:hover td {
  background-color: rgba(255, 179, 0, 0.14) !important;
}

.overdue-row td {
  background-color: rgba(229, 57, 53, 0.06) !important;
}
.overdue-row td:first-child {
  box-shadow: inset 3px 0 0 0 #e53935;
}
.overdue-row:hover td {
  background-color: rgba(229, 57, 53, 0.12) !important;
}

/* Deadline imminente/scaduta: precedenza sull'evidenziazione del follow-up */
.deadline-warn-row td {
  background-color: rgba(251, 140, 0, 0.09) !important;
}
.deadline-warn-row td:first-child {
  box-shadow: inset 3px 0 0 0 #fb8c00;
}
.deadline-warn-row:hover td {
  background-color: rgba(251, 140, 0, 0.16) !important;
}

.deadline-critical-row td {
  background-color: rgba(229, 57, 53, 0.13) !important;
}
.deadline-critical-row td:first-child {
  box-shadow: inset 3px 0 0 0 #b71c1c;
}
.deadline-critical-row:hover td {
  background-color: rgba(229, 57, 53, 0.2) !important;
}
</style>